import type {
  DestinationChannelType,
  DestinationScope,
} from '@/types/provider-destinations';

interface DestinationCandidateLike {
  contact_point_id?: string | null;
  channel_type?: string | null;
  value?: string | null;
}

interface ScopeBlockLike {
  scope?: string | null;
  recommended?: DestinationCandidateLike | null;
  alternates?: DestinationCandidateLike[] | null;
}

interface ProviderServiceDestinationsResponseLike {
  scopes?: ScopeBlockLike[] | null;
}

interface ResolveDestinationContactPointIdParams {
  response: ProviderServiceDestinationsResponseLike;
  scope: DestinationScope;
  channelType: DestinationChannelType;
  channelValue: string;
}

const normalizeFax = (value: string): string => {
  const digitsOnly = value.replace(/\D+/g, '');
  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    return digitsOnly.slice(1);
  }
  return digitsOnly;
};

const normalizeChannelValue = (
  channelType: DestinationChannelType,
  value: string,
): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';

  if (channelType === 'FAX') {
    return normalizeFax(trimmed);
  }
  if (channelType === 'EMAIL' || channelType === 'PORTAL') {
    return trimmed.toLowerCase();
  }

  return trimmed.toLowerCase();
};

const valuesMatch = (
  channelType: DestinationChannelType,
  expectedValue: string,
  actualValue: string | null | undefined,
): boolean => {
  if (!actualValue) return false;

  const normalizedExpected = normalizeChannelValue(channelType, expectedValue);
  const normalizedActual = normalizeChannelValue(channelType, actualValue);
  return normalizedExpected.length > 0 && normalizedExpected === normalizedActual;
};

const findMatchingCandidate = (
  candidates: DestinationCandidateLike[],
  channelType: DestinationChannelType,
  channelValue: string,
): DestinationCandidateLike | null =>
  candidates.find(
    (candidate) =>
      candidate.channel_type === channelType &&
      valuesMatch(channelType, channelValue, candidate.value),
  ) ?? null;

export const resolveDestinationContactPointId = ({
  response,
  scope,
  channelType,
  channelValue,
}: ResolveDestinationContactPointIdParams): string | null => {
  const scopeBlock = response.scopes?.find((entry) => entry.scope === scope);
  if (!scopeBlock) return null;

  const candidates: DestinationCandidateLike[] = [];
  if (scopeBlock.recommended) {
    candidates.push(scopeBlock.recommended);
  }
  if (scopeBlock.alternates?.length) {
    candidates.push(...scopeBlock.alternates);
  }

  const matchedCandidate = findMatchingCandidate(
    candidates,
    channelType,
    channelValue,
  );
  if (!matchedCandidate?.contact_point_id) return null;

  return matchedCandidate.contact_point_id;
};
